import { defineStore } from "pinia";
import { reactive } from "vue";

import axios from "../utils/axios";
import { pusher } from "../plugins/thirdparties/pusher";
import { useAuth } from "./auth";
import type { Patient } from "../components/medicus/PatientBanner";

export const PATIENT_BANNER_THROTTLE_MS = 5000; // reload throttle: leading edge + trailing catch-up
export const PATIENT_BANNER_STALE_MS = 180_000; // 3 min — revalidate on return only after being away longer than this
export const PATIENT_BANNER_CACHE_SIZE = 5; // recently-left patients kept cached (data + subscription)
// ENG-21579 grace window: a re-mount within this of the last unmount is a page navigation, not a
// real "leave" — hopping between banner pages for the same patient never counts as being away.
export const PATIENT_BANNER_LINGER_MS = 250;

interface BannerEntry {
  data: Patient | null;
  loading: boolean;
  error: boolean;
  refs: number;
  throttleTimer: ReturnType<typeof setTimeout> | null;
  pendingReload: boolean;
  dirty: boolean; // an invalidate arrived while cached — refetch on return
  releasedAt: number; // when the last mount let go (0 while in use) — the "away" clock for staleness
  lingerTimer: ReturnType<typeof setTimeout> | null; // grace period before an unmount counts as leaving
}

// ENG-21579 one store owns the fetch + Pusher subscription per patient, so every mount of a
// patient's banner shares one request and channel. Refreshes only on a bodyless "invalidate"
// nudge, not the app-wide reloadData signal.
export const usePatientBanner = defineStore("patient-banner", () => {
  const entries = reactive(new Map<string, BannerEntry>());
  const authStore = useAuth();

  // LRU of recently-left patient ids (oldest first), capped at PATIENT_BANNER_CACHE_SIZE.
  const cachedPatientIds: string[] = [];

  // tenant prefix hand-rolled client-side (as in Chat.vue); PusherService adds it on the backend.
  function channelName(patientId: string) {
    return `${authStore.config?.tenantId}-patient-banner-updated-${patientId}`;
  }

  async function fetchBanner(patientId: string) {
    const entry = entries.get(patientId);
    if (!entry) return;

    if (entry.data === null) entry.loading = true; // block-render on first load only; revalidations keep data visible

    try {
      const { data } = await axios.get<Patient>(
        `/patient/data/patient/patient-banner/${patientId}`,
      );
      entry.data = data;
      entry.error = false;
    } catch {
      entry.error = true;
    } finally {
      entry.loading = false;
    }
  }

  // Leading-edge + trailing-catch-up throttle, per patient.
  function reloadThrottled(patientId: string) {
    const entry = entries.get(patientId);
    if (!entry) return;

    if (entry.throttleTimer) {
      entry.pendingReload = true;
      return;
    }

    fetchBanner(patientId);
    entry.throttleTimer = setTimeout(() => {
      entry.throttleTimer = null;
      if (entry.pendingReload) {
        entry.pendingReload = false;
        reloadThrottled(patientId);
      }
    }, PATIENT_BANNER_THROTTLE_MS);
  }

  function onInvalidate(patientId: string) {
    const entry = entries.get(patientId);
    if (!entry) return;
    if (entry.refs > 0) reloadThrottled(patientId); // in use — refresh now
    else entry.dirty = true; // cached — defer until viewed again
  }

  // pusher tracks the channel by name; we don't keep the Channel object in the reactive entry.
  function subscribe(patientId: string) {
    pusher.subscribe(channelName(patientId)).bind("invalidate", () => onInvalidate(patientId));
  }

  function unsubscribe(patientId: string) {
    const channel = channelName(patientId);
    pusher.channel(channel)?.unbind_all();
    pusher.unsubscribe(channel);
  }

  function acquire(patientId: string) {
    let entry = entries.get(patientId);

    if (!entry) {
      entry = reactive({
        data: null,
        loading: false,
        error: false,
        refs: 0,
        throttleTimer: null,
        pendingReload: false,
        dirty: false,
        releasedAt: 0,
        lingerTimer: null,
      });
      entries.set(patientId, entry);
      subscribe(patientId);
      fetchBanner(patientId);
    } else if (entry.lingerTimer) {
      // Re-mounted inside the grace window — a page navigation, not a real return. Stay
      // continuous: cancel the pending settle and don't revalidate for staleness. A change
      // that landed during the blip still refetches.
      clearTimeout(entry.lingerTimer);
      entry.lingerTimer = null;
      if (entry.dirty) {
        entry.dirty = false;
        fetchBanner(patientId);
      }
    } else if (entry.refs === 0) {
      // Genuinely returning after leaving — render the cached data instantly and revalidate only
      // if it changed while away (dirty) or we were actually away longer than the TTL. Staleness
      // is measured from when we LEFT, not when we fetched, so continuously viewing the patient
      // (any duration) never expires.
      const awayTooLong =
        entry.releasedAt > 0 && Date.now() - entry.releasedAt > PATIENT_BANNER_STALE_MS;
      if (entry.dirty || awayTooLong) {
        entry.dirty = false;
        fetchBanner(patientId);
      }
    }

    entry.releasedAt = 0; // in use
    const cachedAt = cachedPatientIds.indexOf(patientId);
    if (cachedAt !== -1) cachedPatientIds.splice(cachedAt, 1); // in use again
    entry.refs++;
  }

  function release(patientId: string) {
    const entry = entries.get(patientId);
    if (!entry || --entry.refs > 0) return;

    // Stop any in-flight throttle catch-up now the last mount has let go.
    if (entry.throttleTimer) {
      clearTimeout(entry.throttleTimer);
      entry.throttleTimer = null;
    }
    entry.pendingReload = false;

    // Don't treat this as "left" immediately: navigating between two banner pages unmounts then
    // re-mounts within a tick. Defer settling so a quick re-acquire stays continuous.
    if (entry.lingerTimer) clearTimeout(entry.lingerTimer);
    entry.lingerTimer = setTimeout(() => settle(patientId), PATIENT_BANNER_LINGER_MS);
  }

  // The unmount outlasted the grace window — it really counts as leaving now: start the away
  // clock and keep the data + subscription cached (LRU-capped) so a change while away marks it
  // dirty and a longer-than-TTL absence revalidates on return.
  function settle(patientId: string) {
    const entry = entries.get(patientId);
    if (!entry) return;
    entry.lingerTimer = null;
    if (entry.refs > 0) return; // re-acquired during the grace window

    entry.releasedAt = Date.now();
    cachedPatientIds.push(patientId);
    while (cachedPatientIds.length > PATIENT_BANNER_CACHE_SIZE) {
      const evictId = cachedPatientIds.shift()!;
      unsubscribe(evictId);
      entries.delete(evictId);
    }
  }

  return { entries, acquire, release, fetchBanner };
});
